import { enableProdMode } from '@angular/core';
import { applyUpdateV2, Doc as YDoc } from 'yjs';
import { Assets } from 'pixi.js';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { HocuspocusProvider, HocuspocusProviderWebsocket } from "@hocuspocus/provider";
import { Account } from './sync/account';
import { Room } from './sync/room';
import { View } from './graphics/view';
import { Util } from './util/util';
import { IndexDB } from './util/indexdb';
import { Store } from './sync/store';
import { jwtDecode } from 'jwt-decode';
import { platformBrowser } from '@angular/platform-browser';

type LoginContext = {
    hostName?: string;
    roomName?: string;
    password?: string;
};

const elements = {
    loginContainer: document.querySelector<HTMLElement>('#login-container')!,
    appRoot: document.querySelector<HTMLElement>('app-root')!,
    nickname: document.querySelector<HTMLInputElement>('#nickname')!,
    cloneNickname: document.querySelector<HTMLInputElement>('#clone-nickname')!,
    username: document.querySelector<HTMLInputElement>('#username')!,
    userPassword: document.querySelector<HTMLInputElement>('#password')!,
    loginButtons: document.querySelectorAll<HTMLButtonElement>('.login-button')!,
    loginReports: document.querySelectorAll<HTMLElement>('.login-report')!,
    loadingScreen: document.querySelector<HTMLElement>('#loading-screen')!,
    accountToggleCheckbox: document.querySelector<HTMLInputElement>('#use-full-login')!,
    accountToggleCheckboxLabel: document.querySelector<HTMLElement>('#use-full-login-label')!,
    minLoginForm: document.querySelector<HTMLFormElement>('#min-login-form')!,
    fullLoginForm: document.querySelector<HTMLFormElement>('#full-login-form')!,
};

document.addEventListener('DOMContentLoaded', main, { once: true });

async function main() {
    Assets.setPreferences({ crossOrigin: 'anonymous' });

    const context = getContext();
    const url = new URL(window.location.href);

    elements.nickname.value = localStorage.getItem('nickname') ?? getRandomName();
    elements.cloneNickname.value = elements.nickname.value;

    setupPage(context, url);
}

function setupPage(context: LoginContext, url: URL): void {
    const token = localStorage.getItem('login-token');
    if (context.hostName && context.roomName && context.password) {
        if (url.searchParams.has('anon')) {
            url.searchParams.delete('anon');
            window.history.replaceState(null, '', url);
            startup(context);
            return;
        }

        elements.accountToggleCheckboxLabel.classList.remove('hidden');
        if (token) {
            startup(context);
            return;
        }
    } else if (context.hostName && context.roomName) {
        elements.accountToggleCheckbox.checked = true;
        if (token) {
            startup(context);
            return;
        }
    } else if (context.hostName) {
        window.location.replace(`${window.location.origin}`);
    } else {
        const root = window.location.href === window.location.origin + '/';
        if (!root) window.location.replace(window.location.origin);

        elements.accountToggleCheckbox.checked = true;
    }

    resetLoginScreen();
    addEventListeners(context);
}

async function startup(context: LoginContext) {
    const loadingContext = startLoadingScreenDisplay();

    let loginToken = localStorage.getItem("login-token");

    try {
        let userJwt; if (loginToken === null) {
            const username = elements.username.value;
            const userPassword = elements.userPassword.value;

            loginToken = elements.accountToggleCheckbox.checked ? await loginUser(username, userPassword) : await loginAnon();
            userJwt = jwtDecode<{ sub: string, name: string }>(loginToken);
        } else {
            userJwt = jwtDecode<{ sub: string, name: string }>(loginToken);
        }

        const hasHostName = !((context.hostName === null) || (context.hostName === undefined) || (context.hostName === ""));
        const hasRoomName = !((context.roomName === null) || (context.roomName === undefined) || (context.roomName === ""));

        if (!hasHostName) {
            context.hostName = userJwt.name;
            context.roomName = hasRoomName ? context.roomName : 'default';
            history.replaceState({}, '', `/${context.hostName}/${context.roomName}`);
        }

        const isUserHost = userJwt.name === context.hostName;
        const hasPassword = !((context.password === null) || (context.password === undefined) || (context.password === ""));

        if (!hasPassword) {
            if (!isUserHost) {
                resetLoginScreen();
                resetUserLogin("User logged in successfully, but you don't have the room password.");
                elements.loadingScreen.className = 'fadeout';
                return;
            }
        }

        const { roomToken } = await fetchAccessToken<{ roomToken: string }>(`/rooms/${context.hostName}/${context.roomName}`, { body: { roomPassword: context.password ?? null }, token: loginToken });
        const roomJwt = jwtDecode<{ sub: string, owner: string, name: string }>(roomToken);

        const websocket = new HocuspocusProviderWebsocket({ url: environment.websocketUrl });
        const accountProvider = await connectAccount(websocket, userJwt.sub, loginToken, roomToken);

        await new Promise((resolve) => {
            const onSync = () => {
                if (elements.nickname.value === '') elements.nickname.value = getRandomName();
                Account.initialize(userJwt.sub, userJwt.name, accountProvider, elements.nickname.value);
                accountProvider.off('synced', onSync);
                resolve('');
            };
            accountProvider.on('synced', onSync);
        });

        history.replaceState({}, '', `/${context.hostName}/${context.roomName}`);
        await connectRoom(websocket, loginToken, roomToken);
        await View.initialize();

        finishLoadingScreenDisplay(loadingContext);
        startAngular();
    } catch (error) {
        resetLoginScreen();
        resetUserLogin('Login failed. Try again.');
        elements.loginReports.forEach((x) => x.classList.remove('hidden'));
        elements.loadingScreen.className = 'fadeout';
        console.error(error);
    }
}

function startLoadingScreenDisplay() {
    const startupTime = Date.now();
    elements.loginContainer.classList.add('hidden');
    const timeout = setTimeout(() => { elements.loadingScreen.style.display = 'block' }, 500);

    return { startupTime, timeout };
}

function finishLoadingScreenDisplay(loadingContext: { startupTime: number, timeout: NodeJS.Timeout }) {
    elements.appRoot.style.display = 'flex';

    if (Date.now() - loadingContext.startupTime < 500) {
        clearTimeout(loadingContext.timeout);
    } else {
        elements.loadingScreen.className = 'fadeout';
    }
}

function getContext(): LoginContext {
    const routeArray = window.location.pathname.split('/').slice(1);
    const context: LoginContext = {
        hostName: routeArray[0] ? routeArray[0] : undefined,
        roomName: routeArray[1] ? routeArray[1] : undefined,
        password: routeArray[2] ? routeArray[2] : undefined,
    };

    return context;
}

async function loginUser(username: string, password: string) {
    const { token } = await fetchAccessToken<{ token: string }>('/auth/login', { body: { name: username, password } });
    localStorage.setItem('login-token', token);
    return token;
}

async function loginAnon() {
    const { token } = await fetchAccessToken<{ token: string }>('/auth/anon');
    localStorage.setItem('login-token', token);
    return token;
}

async function fetchAccessToken<T>(path: string, options: { method?: string; body?: any; token?: string } = {}): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (options.token) headers['Authorization'] = `Bearer ${options.token}`;

    const response = await fetch(`${environment.hostUrl}${path}`, {
        method: options.method ?? 'POST',
        headers: new Headers(headers),
        body: options.body ? JSON.stringify(options.body) : undefined,
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
    return data;
}

function connectAccount(websocket: HocuspocusProviderWebsocket, accountId: string, userToken: string, roomToken: string): Promise<HocuspocusProvider> {
    return new Promise((resolve, reject) => {
        const roomJwt = jwtDecode(roomToken);
        const accountProvider: HocuspocusProvider = new HocuspocusProvider({
            websocketProvider: websocket,
            name: `${roomJwt.sub}.${accountId}`,
            token: JSON.stringify({ userToken, roomToken }),
            onAuthenticated: () => resolve(accountProvider),
            onAuthenticationFailed: (error) => reject(error),
            onConnect: () => { }
            // TODO: Check if newer files are available
            //    console.info(Array.from(document.scripts).map(x => x.src));
        });
    });
}

function connectRoom(websocket: HocuspocusProviderWebsocket, userToken: string, roomToken: string): Promise<HocuspocusProvider> {
    return new Promise(async (resolve, reject) => {
        const roomJwt = jwtDecode<{ sub: string, owner: string }>(roomToken);
        const localDocumentSave = new YDoc();

        const saveState = await IndexDB.getArrayBuffer(roomJwt.sub);
        if (saveState) applyUpdateV2(localDocumentSave, saveState);

        const roomProvider: HocuspocusProvider = new HocuspocusProvider({
            websocketProvider: websocket,
            name: roomJwt.sub,
            token: JSON.stringify({ userToken, roomToken }),
            document: localDocumentSave,
            onAuthenticated: () => resolve(roomProvider),
            onAuthenticationFailed: (error) => reject(error),
            onStatus: (status) => { Store.connection.next(status.status == 'connected'); },
            onSynced: () => Room.initialize(roomProvider, roomJwt.sub, roomJwt.owner),
            onStateless: ({ payload }) => (window as any).onStateless(JSON.parse(payload)),
        });
    })
}

function addEventListeners(context: LoginContext) {
    elements.nickname.addEventListener('input', () => {
        elements.cloneNickname.value = elements.nickname.value;
    });

    elements.cloneNickname.addEventListener('input', () => {
        elements.nickname.value = elements.cloneNickname.value;
    });

    elements.loginButtons.forEach(e => e.addEventListener('click', () => {
        startup(context);
    }));
}

function startAngular() {
    if (environment.production) {
        enableProdMode();
    }

    platformBrowser().bootstrapModule(AppModule).catch(err => console.error(err));
}

function resetUserLogin(message: string) {
    elements.userPassword.value = '';
    localStorage.removeItem("login-token");

    elements.loginReports.forEach((x) => x.textContent = message);
    elements.loginReports.forEach((x) => x.classList.add('error'));
    elements.loginReports.forEach((x) => x.classList.remove('hidden'));
}

function resetLoginScreen() {
    elements.loginContainer.classList.remove('hidden');
    elements.appRoot.style.display = 'none';
}

function getRandomName() {
    const names = ["Albatross", "Anathema", "Angel", "Antelope", "Apostle", "Armadillo", "Ascendant", "Ascetic", "Assassin", "Banshee", "Barbarian", "Bard", "Barrow", "Basilisk", "Bastion", "Battlemage", "Beast", "Behemoth", "Betrayer", "Binder", "Bound", "Brander", "Breaker", "Bugbear", "Burier", "Butcher", "Cairn", "Caller", "Carver", "Centipede", "Champion", "Changeling", "Channeler", "Chimera", "Chosen", "Cleaver", "Cleric", "Cockatrice", "Conjurer", "Construct", "Corrupter", "Cradle", "Crocodile", "Crone", "Crown", "Crusher", "Cultist", "Curse", "Cyclops", "Deathsinger", "Defiler", "Demon", "Devil", "Devourer", "Djinn", "Doppleganger", "Dragon", "Dreadnought", "Dreamer", "Drowner", "Druid", "Dryad", "Duke", "Dwarf", "Eater", "Echo", "Eidolon", "Elemental", "Elephant", "Elf", "Enchanter", "Ettin", "Executioner", "Exemplar", "Exile", "Exsanguinator", "Fallen", "Fetterer", "Fiend", "Fighter", "Firebeetle", "Flayer", "Flenser", "Forsaken", "Genie", "Ghost", "Ghoul", "Giant", "Gloom", "Goblin", "Golem", "Gorgon", "Gouger", "Gremlin", "Grinder", "Guardian", "Hag", "Hallowborn", "Harpy", "Harrower", "Heir", "Hellhound", "Herald", "Hexblade", "Hippogriff", "Hobgoblin", "Hold", "Hollow", "Homunculus", "Hunter", "Husk", "Hydra", "Idol", "Ifrit", "Imp", "Impaler", "Invoker", "Jackalord", "Judge", "Keep", "Kelpie", "Kin", "King", "Kinless", "Knight", "Kobold", "Kraken", "Lamia", "Leviathan", "Lich", "Lord", "Lurker", "Mangler", "Mark", "Martyr", "Mask", "Maw", "Mercenary", "Monk", "Mummy", "Myconid", "Naga", "Necromancer", "Nightgaunt", "Nomad", "Nymph", "Oathbound", "Occultist", "Ogre", "Oracle", "Orc", "Outcast", "Paladin", "Panthera", "Pariah", "Peeler", "Phantom", "Piercer", "Pilgrim", "Poltergeist", "Porcupine", "Priest", "Prophet", "Pyromancer", "Queen", "Quencher", "Raiser", "Ranger", "Rattlesnake", "Reaper", "Reaver", "Reliquary", "Remnant", "Revenant", "Saint", "Salamander", "Sanctum", "Satyr", "Scarecrow", "Scion", "Scorpion", "Scourge", "Sealer", "Seeker", "Seer", "Sellsword", "Sentinel", "Seraph", "Shade", "Shaman", "Shaper", "Shard", "Silencer", "Skewer", "Slayer", "Sleeper", "Smiter", "Sorcerer", "Spawn", "Specter", "Sphinx", "Spire", "Stalker", "Stallion", "Stranger", "Strangler", "Sunderer", "Supplicant", "Taker", "Tarantula", "Templar", "Thaumaturge", "Thief", "Thorn", "Thornbearer", "Thresher", "Thunderbird", "Tongue", "Troll", "Twin", "Unmaker", "Unseen", "Vampire", "Vault", "Vessel", "Voice", "Votary", "Warden", "Warlock", "Warlord", "Watcher", "Weaver", "Wendigo", "Werewolf", "Witch", "Witness", "Wizard", "Wraith", "Wrecker", "Wyvern"];
    return Util.pick(names);
}
